'use client';
import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import Link from 'next/link';
import {
  Search,
  ArrowUpRight,
  FileJson,
  FileSpreadsheet,
  Calculator,
  Table,
  FileText,
  FileCheck,
  BarChart2,
  Palette,
  Columns,
  Filter,
  GitMerge,
  ArrowUpDown,
  Repeat,
  Calendar,
  XCircle,
  Trash2,
  Percent,
  Shuffle,
  Database,
  Link as LinkIcon,
  List,
} from 'lucide-react';

const tools = [
  {
    icon: Table,
    title: 'CSV Editor',
    description: 'Edit CSV files directly in browser',
    href: '/tools/csv-editor',
    color: 'bg-emerald-500',
  },
  {
    icon: FileText,
    title: 'JSON to CSV Converter',
    description: 'Transform JSON data to CSV',
    href: '/tools/json-to-csv',
    color: 'bg-orange-500',
  },
  {
    icon: FileJson,
    title: 'CSV to JSON Converter',
    description: 'Convert CSV to JSON format',
    href: '/tools/csv-to-json',
    color: 'bg-blue-500',
  },
  {
    icon: FileCheck,
    title: 'Data Type Validator',
    description: 'Check if columns have correct data types',
    href: '/tools/data-type-validator',
    color: 'bg-indigo-500',
  },
  {
    icon: FileSpreadsheet,
    title: 'JSON Formatter',
    description: 'Format and validate JSON data beautifully',
    href: '/tools/json-formatter',
    color: 'bg-purple-500',
  },
  {
    icon: BarChart2,
    title: 'Chart Recommender',
    description: 'Get a suggested chart type for your data',
    href: '/tools/chart-recommender',
    color: 'bg-emerald-500',
  },
  {
    icon: Palette,
    title: 'Color Palette Generator',
    description: 'Create palettes from seed color or samples',
    href: '/tools/color-palette',
    color: 'bg-pink-500',
  },
  {
    icon: Columns,
    title: 'Column Splitter',
    description: 'Split combined columns into separate fields',
    href: '/tools/column-splitter',
    color: 'bg-yellow-500',
  },
  {
    icon: List,
    title: 'Column Summary',
    description: 'Quick per-column summaries',
    href: '/tools/column-summary',
    color: 'bg-sky-500',
  },
  {
    icon: BarChart2,
    title: 'Correlation Calculator',
    description: 'Compute correlation between numeric columns',
    href: '/tools/correlation-calculator',
    color: 'bg-rose-500',
  },
  {
    icon: Filter,
    title: 'Data Filter',
    description: 'Filter rows by simple conditions',
    href: '/tools/data-filter',
    color: 'bg-cyan-500',
  },
  {
    icon: GitMerge,
    title: 'Data Merger',
    description: 'Merge two datasets by key or side-by-side',
    href: '/tools/data-merger',
    color: 'bg-fuchsia-500',
  },
  {
    icon: ArrowUpDown,
    title: 'Data Sorter',
    description: 'Sort CSV rows by column',
    href: '/tools/data-sorter',
    color: 'bg-lime-500',
  },
  {
    icon: Repeat,
    title: 'Data Transpose',
    description: 'Transpose rows and columns',
    href: '/tools/data-transpose',
    color: 'bg-amber-500',
  },
  {
    icon: Calendar,
    title: 'Date Range Calculator',
    description: 'Calculate the number of days between dates',
    href: '/tools/date-range-calculator',
    color: 'bg-teal-500',
  },
  {
    icon: XCircle,
    title: 'Duplicate Remover',
    description: 'Remove duplicate rows easily',
    href: '/tools/duplicate-remover',
    color: 'bg-rose-600',
  },
  {
    icon: Trash2,
    title: 'Empty Row Cleaner',
    description: 'Strip empty rows from your file',
    href: '/tools/empty-row-cleaner',
    color: 'bg-stone-500',
  },
  {
    icon: Percent,
    title: 'Percentage Calculator',
    description: 'Compute percentages quickly',
    href: '/tools/percentage-calculator',
    color: 'bg-indigo-400',
  },
  {
    icon: Calculator,
    title: 'Quick Statistics',
    description: 'Get mean, median and counts instantly',
    href: '/tools/quick-statistics',
    color: 'bg-violet-500',
  },
  {
    icon: Shuffle,
    title: 'Random Sample',
    description: 'Take random samples from your data',
    href: '/tools/random-sample',
    color: 'bg-emerald-400',
  },
  {
    icon: Database,
    title: 'Sample Data Generator',
    description: 'Create demo CSV datasets',
    href: '/tools/sample-data-generator',
    color: 'bg-sky-400',
  },
  {
    icon: LinkIcon,
    title: 'URL Importer',
    description: 'Fetch CSV/text from a remote URL',
    href: '/tools/url-importer',
    color: 'bg-cyan-400',
  },
];

const featuredSeoWorkflows = [
  {
    badge: 'Featured tool page',
    title: 'AI Chart Generator',
    description: 'Turn CSVs, spreadsheets, and exports into presentation-ready charts and summaries.',
    href: '/tools/ai-chart-generator',
    className:
      'border-blue-200/60 bg-blue-50/70 text-blue-600 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-400',
  },
  {
    badge: 'Featured tool page',
    title: 'Bar Chart Generator',
    description: 'Create clean comparison charts from category data in a CSV or spreadsheet.',
    href: '/tools/bar-chart-generator',
    className:
      'border-cyan-200/60 bg-cyan-50/70 text-cyan-600 dark:border-cyan-900/60 dark:bg-cyan-950/30 dark:text-cyan-400',
  },
  {
    badge: 'Featured tool page',
    title: 'Line Graph Generator',
    description: 'Turn time-series exports into trend charts for traffic, revenue, or performance updates.',
    href: '/tools/line-graph-generator',
    className:
      'border-indigo-200/60 bg-indigo-50/70 text-indigo-600 dark:border-indigo-900/60 dark:bg-indigo-950/30 dark:text-indigo-400',
  },
  {
    badge: 'Featured tool page',
    title: 'Pie Chart Generator',
    description: 'Build share-of-total visuals for budgets, channel mix, and category distribution.',
    href: '/tools/pie-chart-generator',
    className:
      'border-amber-200/60 bg-amber-50/70 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-400',
  },
  {
    badge: 'Featured tool page',
    title: 'CSV To Chart',
    description: 'Start with a raw CSV and move to the right chart plus written takeaway faster.',
    href: '/tools/csv-to-chart',
    className:
      'border-violet-200/60 bg-violet-50/70 text-violet-600 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-400',
  },
  {
    badge: 'Featured tool page',
    title: 'Scatter Plot Maker',
    description: 'Show relationships, clusters, and outliers from numeric CSV data in a cleaner visual.',
    href: '/tools/scatter-plot-maker',
    className:
      'border-sky-200/60 bg-sky-50/70 text-sky-600 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-400',
  },
  {
    badge: 'Featured tool page',
    title: 'Box And Whisker Plot Maker',
    description: 'Turn numeric exports into distribution charts that surface quartiles and outliers clearly.',
    href: '/tools/box-and-whisker-plot-maker',
    className:
      'border-rose-200/60 bg-rose-50/70 text-rose-600 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-400',
  },
  {
    badge: 'Featured tool page',
    title: 'Dot Chart Maker',
    description: 'Create compact ranking and benchmark visuals when bars feel too heavy.',
    href: '/tools/dot-chart-maker',
    className:
      'border-lime-200/60 bg-lime-50/70 text-lime-700 dark:border-lime-900/60 dark:bg-lime-950/30 dark:text-lime-400',
  },
  {
    badge: 'Featured tool page',
    title: 'AI Graph Generator',
    description: 'Use AI to pick the right graph and generate a polished visual from a CSV or export.',
    href: '/tools/ai-graph-generator',
    className:
      'border-fuchsia-200/60 bg-fuchsia-50/70 text-fuchsia-600 dark:border-fuchsia-900/60 dark:bg-fuchsia-950/30 dark:text-fuchsia-400',
  },
  {
    badge: 'Featured tool page',
    title: 'Stacked Bar Chart Generator',
    description: 'Show both totals and composition across categories with a segmented bar view.',
    href: '/tools/stacked-bar-chart-generator',
    className:
      'border-orange-200/60 bg-orange-50/70 text-orange-700 dark:border-orange-900/60 dark:bg-orange-950/30 dark:text-orange-400',
  },
  {
    badge: 'Featured template',
    title: 'GA4 Report Template',
    description: 'Turn GA4 exports into trend charts, source breakdowns, and stakeholder-ready notes.',
    href: '/templates/ga4-report-template',
    className:
      'border-emerald-200/60 bg-emerald-50/70 text-emerald-600 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-400',
  },
];

export default function ToolsPage() {
  const [query, setQuery] = useState('');
  const search = query.trim().toLowerCase();
  const filteredTools = tools.filter(tool => `${tool.title} ${tool.description}`.toLowerCase().includes(search));
  const filteredWorkflows = featuredSeoWorkflows.filter(tool =>
    `${tool.title} ${tool.description}`.toLowerCase().includes(search)
  );
  return (
    <div className="marketing-theme marketing-tools-directory">
      <Navbar />
      <main className="marketing-container marketing-directory">
        <header className="marketing-directory-heading">
          <p className="marketing-eyebrow">LESS FRICTION. MORE FLOW.</p>
          <h1>
            Free data tools.
            <br />
            <span>For the work in between.</span>
          </h1>
          <p>Clean a file, convert a format, or check a number. Useful little utilities, without the signup.</p>
        </header>
        <div className="marketing-filter-bar">
          <div>
            <p className="marketing-eyebrow">THE TOOLBOX</p>
            <p>Browser utilities and a next step for your charts.</p>
          </div>
          <label className="marketing-search">
            <Search size={17} aria-hidden="true" />
            <span className="sr-only">Search tools</span>
            <input
              type="search"
              placeholder="Find a tool"
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          </label>
        </div>
        <p className="marketing-results-count" role="status">
          {filteredTools.length + filteredWorkflows.length} tools and workflows
        </p>
        {filteredTools.length > 0 && (
          <section aria-labelledby="utilities-heading" className="marketing-directory-section">
            <div className="marketing-section-line">
              <h2 id="utilities-heading">A quick fix for your data.</h2>
              <span>No account needed</span>
            </div>
            <div className="marketing-tool-grid">
              {filteredTools.map((tool, index) => (
                <Link key={tool.href} href={tool.href} className="marketing-tool-card" data-color={index % 4}>
                  <div className="marketing-tool-card-top">
                    <tool.icon size={25} strokeWidth={1.6} aria-hidden="true" />
                    <ArrowUpRight size={18} aria-hidden="true" />
                  </div>
                  <h3>{tool.title}</h3>
                  <p>{tool.description}</p>
                  <span className="marketing-tool-card-label">Open tool</span>
                </Link>
              ))}
            </div>
          </section>
        )}
        {filteredWorkflows.length > 0 && (
          <section aria-labelledby="workflows-heading" className="marketing-directory-section">
            <div className="marketing-section-line">
              <div>
                <p className="marketing-eyebrow">THE BIGGER PICTURE</p>
                <h2 id="workflows-heading">Ready to make a chart?</h2>
              </div>
              <span>Continues in your AI workspace</span>
            </div>
            <p className="marketing-section-intro">
              These guided workflows use your PlotsAlot account and its AI generation limits. Start free, then choose
              more capacity if you need it.
            </p>
            <div className="marketing-tool-grid">
              {filteredWorkflows.map((tool, index) => (
                <Link
                  key={tool.href}
                  href={tool.href}
                  className="marketing-tool-card marketing-workflow-card"
                  data-color={index % 4}>
                  <div className="marketing-tool-card-top">
                    <BarChart2 size={25} strokeWidth={1.6} aria-hidden="true" />
                    <ArrowUpRight size={18} aria-hidden="true" />
                  </div>
                  <h3>{tool.title}</h3>
                  <p>{tool.description}</p>
                  <span className="marketing-tool-card-label">Explore workflow</span>
                </Link>
              ))}
            </div>
          </section>
        )}
        {filteredTools.length + filteredWorkflows.length === 0 && (
          <div className="marketing-empty">
            <h2>No tools found.</h2>
            <p>Try CSV, chart, color or statistics.</p>
            <button type="button" className="marketing-button marketing-button-white" onClick={() => setQuery('')}>
              Clear search
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
